import { useState } from 'react';
import type { FormEvent } from 'react';

import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { DeliveryBadge, PurchaseOrderBadge } from '@/components/StatusBadge';
import { shortDate, today } from '@/lib/format';
import { usePurchaseActions, usePurchaseOrders } from '@/lib/queries';
import { useSite } from '@/lib/useSite';
import type { PurchaseOrder } from '@/lib/types';

/**
 * 구매 발주 수신 · 납품 · 검수.
 *
 * <p><b>발주를 만드는 자리가 없다.</b> PO 는 본사(외주구매팀)가 내는 것이라 협력사는 받는다 —
 * 없는 기능의 칸을 화면에 세우지 않는다.
 *
 * <p>납품은 협력사가 올리고 검수는 감리가 누른다. 둘을 한 주체가 하면 「내가 넣고 내가 합격」이
 * 되어 검수가 기록일 뿐 판정이 아니게 된다.
 */
export function PurchaseOrdersPage() {
  // 납품은 협력사만, 검수는 판정하는 손(감리·직원)이다(DOC-87 §2-3).
  const { siteId, siteName, isJudge, isVendor } = useSite();
  const orders = usePurchaseOrders(siteId);
  const { deliver, inspect } = usePurchaseActions(siteId);

  const [deliveringFor, setDeliveringFor] = useState<string | null>(null);
  const [deliveredOn, setDeliveredOn] = useState(today);
  const [quantity, setQuantity] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');

  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const rows = orders.data ?? [];

  async function onDeliver(event: FormEvent, orderId: string) {
    event.preventDefault();
    await deliver.mutateAsync({
      orderId,
      deliveredOn,
      quantity: Number(quantity.replace(/[^0-9.]/g, '')),
      invoiceNo: invoiceNo.trim() === '' ? null : invoiceNo.trim(),
    });
    setDeliveringFor(null);
    setQuantity('');
    setInvoiceNo('');
  }

  async function onReject(event: FormEvent, deliveryId: string) {
    event.preventDefault();
    await inspect.mutateAsync({ deliveryId, result: '불합격', note: note.trim() });
    setRejectingId(null);
    setNote('');
  }

  return (
    <>
      <PageHeader title="구매·납품" subtitle={siteName} />

      <QueryState
        pending={orders.isPending}
        error={orders.isError}
        empty={rows.length === 0}
        emptyText="수신된 발주가 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {rows.map((order) => (
            <li key={order.id} className="card">
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-ui-md font-medium">
                    {order.itemName}
                    {order.spec !== null && (
                      <span className="ml-1 text-ui-2xs" style={{ color: 'var(--text-muted)' }}>{order.spec}</span>
                    )}
                  </div>
                  <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                    {order.orderNo} · 납기 {shortDate(order.dueDate)}
                  </div>
                </div>
                <PurchaseOrderBadge status={order.status} />
              </div>

              <div className="mb-1 flex items-end justify-between gap-3">
                <span className="text-ui-lg font-semibold">
                  {order.deliveredQuantity.toLocaleString('ko-KR')} / {order.quantity.toLocaleString('ko-KR')}
                  <span className="ml-1 text-ui-xs font-normal">{order.unit}</span>
                </span>
                {isLate(order) && (
                  <span className="text-ui-2xs font-medium" style={{ color: 'var(--danger)' }}>납기 초과</span>
                )}
              </div>

              {order.deliveries.length > 0 && (
                <ul className="mt-2 flex flex-col border-t pt-1" style={{ borderColor: 'var(--border)' }}>
                  {order.deliveries.map((row) => (
                    <li key={row.id} className="py-1.5">
                      <div className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-ui-sm">
                          {shortDate(row.deliveredOn)} · {row.quantity.toLocaleString('ko-KR')}{order.unit}
                          {row.invoiceNo !== null && (
                            <span className="ml-1 text-ui-2xs" style={{ color: 'var(--text-muted)' }}>{row.invoiceNo}</span>
                          )}
                        </span>
                        <DeliveryBadge result={row.result} />
                      </div>

                      {row.note !== null && (
                        <p className="mt-1 rounded-md p-2 text-ui-sm" style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}>
                          {row.note}
                        </p>
                      )}

                      {isJudge && row.result === '검수대기' && (
                        rejectingId === row.id ? (
                          <form onSubmit={(e) => onReject(e, row.id)} className="mt-2">
                            <textarea
                              className="input-field mb-2"
                              rows={2}
                              value={note}
                              onChange={(e) => setNote(e.target.value)}
                              placeholder="불합격 사유"
                              required
                            />
                            <div className="flex gap-2">
                              <button type="submit" className="btn-secondary flex-1" style={{ color: 'var(--danger)' }} disabled={inspect.isPending || note.trim() === ''}>
                                불합격 처리
                              </button>
                              <button type="button" className="btn-secondary" onClick={() => setRejectingId(null)}>취소</button>
                            </div>
                          </form>
                        ) : (
                          <div className="mt-2 flex gap-2">
                            <button
                              type="button"
                              className="btn-primary flex-1"
                              onClick={() => inspect.mutate({ deliveryId: row.id, result: '합격', note: null })}
                              disabled={inspect.isPending}
                            >
                              합격
                            </button>
                            <button
                              type="button"
                              className="btn-secondary flex-1"
                              style={{ color: 'var(--danger)' }}
                              onClick={() => { setRejectingId(row.id); setNote(''); }}
                            >
                              불합격
                            </button>
                          </div>
                        )
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {isVendor && order.deliveredQuantity < order.quantity && (
                deliveringFor === order.id ? (
                  <form onSubmit={(e) => onDeliver(e, order.id)} className="mt-3">
                    <div className="mb-2 flex gap-2">
                      <label className="flex-1">
                        <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>납품일</span>
                        <input type="date" className="input-field" value={deliveredOn} onChange={(e) => setDeliveredOn(e.target.value)} required />
                      </label>
                      <label className="flex-1">
                        <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>수량 ({order.unit})</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="input-field"
                          value={quantity}
                          onChange={(e) => setQuantity(e.target.value)}
                          placeholder="0"
                          required
                        />
                      </label>
                    </div>
                    <label className="mb-3 block">
                      <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>거래명세서 번호</span>
                      <input type="text" className="input-field" value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
                    </label>
                    <div className="flex gap-2">
                      <button type="submit" className="btn-primary flex-1" disabled={deliver.isPending}>
                        {deliver.isPending ? '보내는 중입니다…' : '납품 등록'}
                      </button>
                      <button type="button" className="btn-secondary" onClick={() => setDeliveringFor(null)}>취소</button>
                    </div>
                  </form>
                ) : (
                  <button
                    type="button"
                    className="btn-secondary mt-3 w-full"
                    onClick={() => { setDeliveringFor(order.id); setQuantity(''); setInvoiceNo(''); setDeliveredOn(today()); }}
                  >
                    납품 등록
                  </button>
                )
              )}
            </li>
          ))}
        </ul>
      </QueryState>
    </>
  );
}

/** 납기를 넘겼는데 아직 다 안 들어온 발주. */
function isLate(order: PurchaseOrder): boolean {
  return order.dueDate < today() && order.deliveredQuantity < order.quantity;
}
